/**
 * feature-access — central helper for resolving plan-gated features.
 *
 * Single source of truth (read order):
 *   1. The active plan (Pro/Basic) — resolved from `academy.plan_status`
 *      ('trial' inherits via matrix.trial_inherits, default 'pro').
 *   2. The plan_feature_matrix row, resolved via capabilities:
 *        granular key → KEY_TO_CAPABILITY[key] → plans[plan].capabilities[cap]
 *      Falls back to the legacy `plans[plan].features[key]` only when the
 *      capabilities block is not present (transitional safety net).
 *
 * If the matrix is missing or the key is not present, we FAIL CLOSED for
 * Pro-only safety: returns false. Callers should treat `null` matrix as
 * "still loading" and avoid rendering blocked state until ready.
 */
import { KEY_TO_CAPABILITY, CORE_CAPABILITIES } from "./capabilities";

/**
 * Resolve the effective plan for an academy.
 * Trial → matrix.trial_inherits (default 'pro').
 */
export function resolveEffectivePlan(academy, matrix) {
  if (!academy) return null;
  const status = academy.plan_status;
  // Phase 25 — never silently fall back to "basic" when plan/plan_status
  // are absent. The student dashboard shell now gates on
  // `planContextHydrated`, but defending here too prevents any caller
  // that bypasses the gate from showing locked Pro features for a Pro
  // academy whose plan field hasn't merged yet. Returning `null` makes
  // `canUseFeature` fail-closed AND distinguishable from a real Basic.
  if (!academy.plan && !academy.plan_status) return null;
  const plan = academy.plan || "basic";
  if (status === "trial") {
    const inherits = matrix?.trial_inherits;
    // "plan" (or unset) means: inherit the academy's actual plan.
    // Any other value (e.g. legacy "pro") forces that plan during trial.
    if (!inherits || inherits === "plan") {
      return plan === "pro" ? "pro" : "basic";
    }
    return inherits === "pro" ? "pro" : "basic";
  }
  return plan === "pro" ? "pro" : "basic";
}

/**
 * isPlanResolvable — true when the academy row carries enough plan
 * information for `resolveEffectivePlan` to return a real plan
 * (i.e. NOT null). Used by the student auth hook to decide whether
 * the dashboard render gate may release.
 */
export function isPlanResolvable(academy) {
  if (!academy) return false;
  return !!(academy.plan || academy.plan_status);
}

/**
 * canUseFeature(featureKey, academy, matrix) → boolean
 *
 * Returns true if the academy's effective plan includes the feature key
 * in the matrix. Returns false if matrix not loaded yet or key missing.
 */
export function canUseFeature(featureKey, academy, matrix) {
  if (!featureKey || !academy || !matrix) return false;
  const plan = resolveEffectivePlan(academy, matrix);
  if (!plan) return false;
  const planBlock = matrix?.plans?.[plan];
  if (!planBlock) return false;

  // Primary path: resolve via capability.
  const capKey = KEY_TO_CAPABILITY[featureKey];
  if (capKey && planBlock.capabilities) {
    if (CORE_CAPABILITIES.has(capKey)) return true;
    return planBlock.capabilities[capKey] === true;
  }

  // Backwards-compatible fallback to flat features map (pre-migration).
  const flags = planBlock.features;
  return flags?.[featureKey] === true;
}

/**
 * Inverse helper used by gates.
 * Returns true if feature is NOT available on the current plan.
 */
export function requiresUpgrade(featureKey, academy, matrix) {
  if (!matrix || !academy) return false; // unknown → don't lock
  return !canUseFeature(featureKey, academy, matrix);
}

/**
 * isPlanExpired — true if the academy lost active access (expired/cancelled/suspended).
 */
export function isPlanExpired(academy) {
  if (!academy?.plan_status) return false;
  return ["expired", "cancelled", "suspended"].includes(academy.plan_status);
}

/**
 * isPlanPastDue — billing failed but still in grace.
 */
export function isPlanPastDue(academy) {
  return academy?.plan_status === "past_due";
}

/**
 * isProLikePlan — convenience boolean: the academy is currently entitled
 * to Pro-tier features either because it sits on the Pro plan OR because
 * it is in an active trial (trials inherit Pro by default — see
 * `resolveEffectivePlan`). Used by lightweight callers that don't need
 * a per-feature lookup. For real feature gating always prefer
 * `canUseFeature(featureKey, academy, matrix)`.
 */
export function isProLikePlan(academy, matrix) {
  if (!academy) return false;
  return resolveEffectivePlan(academy, matrix) === "pro";
}

/**
 * pickFirstUnlockedTab — given a list of tab descriptors, returns the key
 * of the first tab whose featureKey is allowed on the current academy plan.
 *
 * tabs: [{ key, featureKey?: string|null }]
 *   - tabs without `featureKey` are treated as always-unlocked (Basic-safe)
 *
 * Behaviour:
 *   - If matrix is not yet loaded → returns the first tab with NO featureKey
 *     (fail-closed: never default into a possibly-locked tab).
 *   - If every tab is locked → returns null (caller should render a
 *     module-level locked state instead of mounting a tab).
 */
export function pickFirstUnlockedTab(tabs, academy, matrix) {
  if (!Array.isArray(tabs) || tabs.length === 0) return null;
  // No academy yet — be safe, prefer a tab with no featureKey.
  if (!academy) {
    const free = tabs.find((t) => !t.featureKey);
    return free ? free.key : null;
  }
  // Matrix still loading — fail closed: only return a tab with no featureKey.
  if (!matrix) {
    const free = tabs.find((t) => !t.featureKey);
    return free ? free.key : null;
  }
  for (const tab of tabs) {
    if (!tab.featureKey) return tab.key;
    if (canUseFeature(tab.featureKey, academy, matrix)) return tab.key;
  }
  return null;
}